import { NadoWithdrawRoute } from './withdrawRoutes';

export function getExpectedNadoWithdrawBridgeInputAmount({
  parsedAmount,
  feeRaw,
}: {
  parsedAmount: bigint | undefined;
  feeRaw: bigint | undefined;
}): bigint | undefined {
  if (parsedAmount === undefined || feeRaw === undefined) {
    return undefined;
  }
  return parsedAmount > feeRaw ? parsedAmount - feeRaw : BigInt("0");
}

export function canCreateLockedWithdrawQuote({
  route,
  parsedAmount,
  feeRaw,
  address,
}: {
  route: NadoWithdrawRoute | undefined;
  parsedAmount: bigint | undefined;
  feeRaw: bigint | undefined;
  address: string | undefined;
}) {
  return !!route && !!address && !!parsedAmount && feeRaw !== undefined && parsedAmount > feeRaw;
}

export function buildNadoWithdrawAcrossQuoteParams({
  route,
  lockedAddress,
  expectedBridgeInputAmount,
}: {
  route: NadoWithdrawRoute | undefined;
  lockedAddress: `0x${string}` | undefined;
  expectedBridgeInputAmount: bigint | undefined;
}) {
  if (!route || !lockedAddress || !expectedBridgeInputAmount || expectedBridgeInputAmount <= BigInt("0")) {
    return {
      sourceChainId: undefined,
      destinationChainId: undefined,
      inputToken: undefined,
      outputToken: undefined,
      amount: undefined,
      recipient: undefined,
    };
  }

  return {
    sourceChainId: 57073,
    destinationChainId: route.destinationChainId,
    inputToken: route.inkInputToken,
    outputToken: route.destinationOutputToken,
    amount: expectedBridgeInputAmount,
    recipient: lockedAddress,
  };
}

export function getNadoWithdrawInkArrivalState({
  preWithdrawBalance,
  currentInkBalance,
  expectedBridgeInputAmount,
}: {
  preWithdrawBalance: bigint | undefined;
  currentInkBalance: bigint | undefined;
  expectedBridgeInputAmount: bigint | undefined;
}): {
  status: 'waiting' | 'ready';
  delta: bigint | undefined;
  bridgeAmount: bigint | undefined;
} {
  if (currentInkBalance === undefined || expectedBridgeInputAmount === undefined) {
    return { status: 'waiting', delta: undefined, bridgeAmount: undefined };
  }

  if (preWithdrawBalance === undefined) {
    if (currentInkBalance >= expectedBridgeInputAmount) {
      return {
        status: 'ready',
        delta: undefined,
        bridgeAmount: expectedBridgeInputAmount,
      };
    }
    return { status: 'waiting', delta: undefined, bridgeAmount: undefined };
  }

  const delta =
    currentInkBalance > preWithdrawBalance
      ? currentInkBalance - preWithdrawBalance
      : BigInt("0");

  if (delta >= expectedBridgeInputAmount) {
    return {
      status: 'ready',
      delta,
      bridgeAmount: expectedBridgeInputAmount,
    };
  }

  return { status: 'waiting', delta, bridgeAmount: undefined };
}

export function getNadoWithdrawDestinationArrivalState({
  preDestinationBalance,
  currentDestinationBalance,
  expectedOutputAmount,
  submittedAtMs,
  nowMs,
  timeoutMs = 180_000,
}: {
  preDestinationBalance: bigint | undefined;
  currentDestinationBalance: bigint | undefined;
  expectedOutputAmount: bigint | undefined;
  submittedAtMs?: number;
  nowMs?: number;
  timeoutMs?: number;
}): {
  status: 'missing_baseline' | 'waiting' | 'delivered' | 'partial_arrival' | 'arrival_timeout';
  delta: bigint | undefined;
} {
  if (preDestinationBalance === undefined) {
    return { status: 'missing_baseline', delta: undefined };
  }

  const delta =
    currentDestinationBalance !== undefined && currentDestinationBalance > preDestinationBalance
      ? currentDestinationBalance - preDestinationBalance
      : BigInt("0");

  if (expectedOutputAmount === undefined) {
    if (submittedAtMs !== undefined && nowMs !== undefined && nowMs - submittedAtMs >= timeoutMs) {
      return {
        status: delta > BigInt("0") ? 'partial_arrival' : 'arrival_timeout',
        delta,
      };
    }
    return { status: 'waiting', delta };
  }

  if (delta >= expectedOutputAmount) {
    return { status: 'delivered', delta };
  }

  if (submittedAtMs !== undefined && nowMs !== undefined && nowMs - submittedAtMs >= timeoutMs) {
    return {
      status: delta > BigInt("0") ? 'partial_arrival' : 'arrival_timeout',
      delta,
    };
  }

  return { status: 'waiting', delta };
}

export function shouldCaptureNadoWithdrawDestinationBaseline({
  withdrawComplete,
  preDestinationBalance,
  currentDestinationBalance,
}: {
  withdrawComplete: boolean;
  preDestinationBalance: bigint | undefined;
  currentDestinationBalance: bigint | undefined;
}) {
  return withdrawComplete && preDestinationBalance === undefined && currentDestinationBalance !== undefined;
}

export function getNadoWithdrawWalletState({
  lockedAddress,
  currentAddress,
  hasStartedTransaction,
}: {
  lockedAddress: string | undefined;
  currentAddress: string | undefined;
  hasStartedTransaction: boolean;
}): 'unlocked' | 'matched' | 'reset' | 'mismatch' {
  if (!lockedAddress) {
    return 'unlocked';
  }
  if (lockedAddress.toLowerCase() === currentAddress?.toLowerCase()) {
    return 'matched';
  }
  return hasStartedTransaction ? 'mismatch' : 'reset';
}
